/**
 * ADR-0224 — Releases: the hub admin page (spec §3.1–§3.5; impl plan Phase 1
 * step 1.4; the approved mock-up CWEfYDwKZmyQhZjKngq6VT v3). Four tabs as
 * route segments: Planned · In flight · Shipped (default) · This week. The
 * page body carries the meeting decks' identity (§3.6, the owner-ruled
 * exception shared with ADR-0223: `--collab-*` tokens, `collab*` classes);
 * the admin shell and its nav stay as they are.
 *
 * Every sentence a group shows — date, time, result, recorded-by — is the
 * hub's (src/releases/texts.ts); this file lays it out and takes the admin's
 * gestures: Record a change (the composer), inline meaning lines (autosave on
 * blur), Plan a deploy (a quick create), Record a maintenance window, where a
 * deploy was announced, Remove (planned or in flight only). Mark shipped is
 * not offered: a change ships when its deploy is recorded from git, by hand
 * through the door's record_deploy (errata E18 — nothing records by itself).
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState, EmptyState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { apiFetch } from "../lib/api.js";
import {
  KIND_WORDS,
  parseRefInput,
  plannedAt,
  pullRequestRuns,
  refLinks,
  refsBody,
  RELEASE_REPOS,
  RELEASES_TABS,
  type Change,
  type Counts,
  type DeployGroup,
  type ReleaseRepo,
  type ReleasesTab,
  type WeekView,
} from "../lib/releases.js";

// ─────────────────────────────────────────────────────────────────────────────
// Shared bits
// ─────────────────────────────────────────────────────────────────────────────

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function Token(props: { kind: "deploy" | "hotfix" | "maintenance" | "in_flight" | "planned" }) {
  const cls = { deploy: "collabTokShipped", hotfix: "collabTokHotfix", maintenance: "collabTokMaint", in_flight: "collabTokFlight", planned: "collabTokPlanned" }[props.kind];
  const word = props.kind === "in_flight" ? "In flight" : props.kind === "planned" ? "Planned" : KIND_WORDS[props.kind];
  return <span className={`collabTok ${cls}`}>{word}</span>;
}

function RepoChips(props: { repos: readonly ReleaseRepo[] }) {
  return (
    <>
      {props.repos.map((r) => (
        <span key={r} className="collabRepo">
          {r}
        </span>
      ))}
    </>
  );
}

function RefChips(props: { change: Change }) {
  return (
    <>
      {refLinks(props.change.refs).map((r, i) =>
        r.url ? (
          <a key={i} className="collabPr" href={r.url} target="_blank" rel="noreferrer" title={`${r.repo} ${r.label} — opens GitHub (sign-in required)`}>
            {r.label}
          </a>
        ) : (
          <span key={i} className="collabPr" title={r.repo}>
            {r.label}
          </span>
        )
      )}
    </>
  );
}

/** The meaning line, editable in place; saves when the admin leaves the field (UI_GUIDELINES §1.6). */
function MeaningLine(props: { change: Change; onSaved: (c: Change) => void }) {
  const toast = useToast();
  const [value, setValue] = useState(props.change.meaning);
  const [editing, setEditing] = useState(false);
  useEffect(() => setValue(props.change.meaning), [props.change.meaning]);
  const save = async () => {
    setEditing(false);
    const next = value.trim();
    if (!next || next === props.change.meaning) {
      setValue(props.change.meaning);
      return;
    }
    try {
      const r = await apiFetch<{ change: Change }>(`/api/admin/releases/changes/${encodeURIComponent(props.change.id)}`, { method: "PATCH", body: { meaning: next } });
      props.onSaved(r.change);
    } catch (e) {
      toast.push({ kind: "error", message: errorMessage(e) });
      setValue(props.change.meaning);
    }
  };
  if (!editing) {
    return (
      <button type="button" className="collabLine collabLineEdit" title="Edit the meaning line" onClick={() => setEditing(true)}>
        {props.change.meaning}
      </button>
    );
  }
  return (
    <textarea
      className="input collabLineInput"
      autoFocus
      value={value}
      maxLength={300}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => void save()}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
        if (e.key === "Escape") {
          setValue(props.change.meaning);
          setEditing(false);
        }
      }}
    />
  );
}

/** The feature name, editable in place on an Unnamed row (§5.3 — the developer names it). */
function FeatureName(props: { change: Change; onSaved: (c: Change) => void }) {
  const toast = useToast();
  const [value, setValue] = useState(props.change.unnamed ? "" : props.change.feature);
  const [editing, setEditing] = useState(false);
  useEffect(() => setValue(props.change.unnamed ? "" : props.change.feature), [props.change.feature, props.change.unnamed]);
  if (!props.change.unnamed && !editing) return <div className="collabFeat">{props.change.feature}</div>;
  const save = async () => {
    setEditing(false);
    const next = value.trim();
    if (!next || next === props.change.feature) return;
    try {
      const r = await apiFetch<{ change: Change }>(`/api/admin/releases/changes/${encodeURIComponent(props.change.id)}`, { method: "PATCH", body: { feature: next } });
      props.onSaved(r.change);
    } catch (e) {
      toast.push({ kind: "error", message: errorMessage(e) });
    }
  };
  if (!editing) {
    return (
      <button type="button" className="collabFeat collabFeatUnnamed" onClick={() => setEditing(true)} title="Name this change">
        Unnamed change — name it
      </button>
    );
  }
  return <input className="input collabFeatInput" autoFocus value={value} maxLength={120} placeholder="Feature name" onChange={(e) => setValue(e.target.value)} onBlur={() => void save()} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} />;
}

function ChangeRow(props: { change: Change; onSaved: (c: Change) => void; token?: "in_flight" | "planned"; onRemove?: () => void }) {
  const c = props.change;
  return (
    <li className={`collabRow${c.unnamed ? " collabRowUnnamed" : ""}`}>
      <div>
        <FeatureName change={c} onSaved={props.onSaved} />
        <MeaningLine change={c} onSaved={props.onSaved} />
      </div>
      <div className="collabRowMeta">
        {props.token ? <Token kind={props.token} /> : null}
        <RepoChips repos={c.repos} />
        <RefChips change={c} />
        {props.onRemove ? (
          <button type="button" className="adminListAction isDanger" onClick={props.onRemove}>
            Remove
          </button>
        ) : null}
      </div>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// A deploy group (§3.2)
// ─────────────────────────────────────────────────────────────────────────────

function AnnouncedLine(props: { group: DeployGroup; onSaved: (g: DeployGroup) => void }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(props.group.announced_where ?? "");
  useEffect(() => setValue(props.group.announced_where ?? ""), [props.group.announced_where]);
  const save = async () => {
    setEditing(false);
    const next = value.trim();
    if (next === (props.group.announced_where ?? "")) return;
    try {
      const r = await apiFetch<{ deploy: DeployGroup }>(`/api/admin/releases/deploys/${encodeURIComponent(props.group.id)}`, { method: "PATCH", body: { announced_where: next || null } });
      props.onSaved(r.deploy);
    } catch (e) {
      toast.push({ kind: "error", message: errorMessage(e) });
    }
  };
  if (editing) {
    return (
      <span>
        <b>Announced</b> <input className="input collabFootInput" autoFocus value={value} maxLength={200} placeholder="where, and when" onChange={(e) => setValue(e.target.value)} onBlur={() => void save()} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} />
      </span>
    );
  }
  return (
    <button type="button" className="collabFootEdit" onClick={() => setEditing(true)} title="Where this deploy was announced">
      <b>Announced</b> {props.group.announced_where ?? (props.group.kind === "maintenance" ? "not announced" : "—")}
    </button>
  );
}

function DeployGroupCard(props: { group: DeployGroup; onChanged: (g: DeployGroup) => void }) {
  const g = props.group;
  const onSavedChange = (c: Change) => props.onChanged({ ...g, changes: g.changes.map((x) => (x.id === c.id ? c : x)) });
  const runs = pullRequestRuns(g.pull_requests);
  return (
    <article className="collabDeploy">
      <div className="collabDeployHead">
        <span className="collabDeployDate">{g.date_words}</span>
        <span className="collabDeployMeta">
          {g.kind === "maintenance" ? (
            <>
              maintenance window · {g.time_words}
              {g.ended_at ? " onward" : ""} · the droplet
            </>
          ) : (
            <>
              {g.state === "planned" ? "planned for " : ""}
              {g.time_words}
              {runs.map((run) => (
                <span key={run.repo}>
                  {" · "}
                  <b>{run.repo}</b>{" "}
                  {run.prs.map((p) => (
                    <a key={p.number} className="collabPr" href={p.url} target="_blank" rel="noreferrer" title={p.title ? `${p.title} — opens GitHub (sign-in required)` : "opens GitHub (sign-in required)"}>
                      #{p.number}
                    </a>
                  ))}
                </span>
              ))}
              {g.result_words ? ` · ${g.result_words}` : ""}
              {g.commit ? (
                <>
                  {" · "}
                  <span className="collabCommit mono" title="the hub commit deployed">
                    {g.commit.slice(0, 7)}
                  </span>
                </>
              ) : null}
            </>
          )}
        </span>
        <div className="collabDeployRight">
          <Token kind={g.state === "planned" ? "planned" : g.kind} />
        </div>
      </div>
      {g.changes.length ? (
        <ul className="collabRows">
          {g.changes.map((c) => (
            <ChangeRow key={c.id} change={c} onSaved={onSavedChange} token={g.state === "planned" ? (c.state === "in_flight" ? "in_flight" : "planned") : undefined} />
          ))}
        </ul>
      ) : (
        <div className="collabRowsEmpty">No change recorded under this deploy.</div>
      )}
      {g.state === "shipped" ? (
        <div className="collabDeployFoot">
          <AnnouncedLine group={g} onSaved={props.onChanged} />
          <span>
            <b>Recorded by</b> {g.recorded_words}
          </span>
        </div>
      ) : null}
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shipped (§3.2)
// ─────────────────────────────────────────────────────────────────────────────

function ShippedTab(props: { onCounts: (c: Counts) => void }) {
  const toast = useToast();
  const [groups, setGroups] = useState<DeployGroup[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);

  const load = async (before: string | null) => {
    try {
      const r = await apiFetch<{ deploys: DeployGroup[]; next: string | null; counts: Counts }>(`/api/admin/releases?state=shipped${before ? `&before=${encodeURIComponent(before)}` : ""}`);
      setGroups((prev) => (before ? [...prev, ...r.deploys] : r.deploys));
      setNext(r.next);
      props.onCounts(r.counts);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
      setBusy(false);
    }
  };
  useEffect(() => {
    void load(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const replace = (g: DeployGroup) => setGroups((prev) => prev.map((x) => (x.id === g.id ? g : x)));

  if (error) return <ErrorState detail={error} />;
  if (loading) return <LoadingState />;
  return (
    <div>
      <div className="collabSecHead">
        <span className="collabCount">By deploy, newest first</span>
        <button type="button" className="adminNewLink" onClick={() => setMaintenanceOpen(true)}>
          + Record a maintenance window
        </button>
      </div>
      {maintenanceOpen ? (
        <MaintenanceDialog
          onClose={() => setMaintenanceOpen(false)}
          onRecorded={(g) => {
            setMaintenanceOpen(false);
            setGroups((prev) => [g, ...prev].sort((a, b) => b.at.localeCompare(a.at)));
            toast.push({ kind: "success", message: "Maintenance window recorded." });
            void load(null);
          }}
        />
      ) : null}
      {groups.length === 0 ? (
        <EmptyState title="Nothing has shipped yet — a deploy appears here once it is recorded from git through the door." />
      ) : (
        <div className="collabDeployList">
          {groups.map((g) => (
            <DeployGroupCard key={g.id} group={g} onChanged={replace} />
          ))}
        </div>
      )}
      {next ? (
        <div className="actions" style={{ marginTop: 14 }}>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void load(next);
            }}
          >
            Load more
          </button>
        </div>
      ) : null}
    </div>
  );
}

function MaintenanceDialog(props: { onClose: () => void; onRecorded: (g: DeployGroup) => void }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [meaning, setMeaning] = useState("");
  const [announced, setAnnounced] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const at = plannedAt(date, time);
  const endedAt = endTime ? plannedAt(date, endTime) : null;
  const submit = async () => {
    if (!at || !meaning.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiFetch<{ deploy: DeployGroup }>("/api/admin/releases/deploys/maintenance", {
        method: "POST",
        body: { at, ended_at: endedAt, meaning: meaning.trim(), announced_where: announced.trim() || null },
      });
      props.onRecorded(r.deploy);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <div className="modalOverlay" onClick={props.onClose}>
      <div className="modal collabModal" role="dialog" aria-modal="true" aria-label="Record a maintenance window" onClick={(e) => e.stopPropagation()}>
        <h2 className="collabH2">Record a maintenance window</h2>
        <div style={{ display: "grid", gap: 10 }}>
          <div className="inputRow">
            <label className="field">
              <span className="label">Date</span>
              <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <label className="field">
              <span className="label">From</span>
              <input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </label>
            <label className="field">
              <span className="label">To</span>
              <input className="input" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </label>
          </div>
          <label className="field">
            <span className="label">What was done, in one line a user would understand</span>
            <textarea className="input" rows={2} maxLength={300} value={meaning} onChange={(e) => setMeaning(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Announced where (blank = not announced)</span>
            <input className="input" maxLength={200} value={announced} onChange={(e) => setAnnounced(e.target.value)} />
          </label>
          {error ? <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div> : null}
          <div className="actions">
            <button type="button" className="btn" onClick={props.onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btnPrimary" onClick={() => void submit()} disabled={busy || !at || !meaning.trim()}>
              Record
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// In flight (§3.3) — the composer and the rows
// ─────────────────────────────────────────────────────────────────────────────

function Composer(props: { featureNames: string[]; onRecorded: (c: Change) => void; autoFocus?: boolean }) {
  const [meaning, setMeaning] = useState("");
  const [expanded, setExpanded] = useState(!!props.autoFocus);
  const [feature, setFeature] = useState("");
  const [repos, setRepos] = useState<ReleaseRepo[]>([]);
  const [refsText, setRefsText] = useState("");
  const [state, setState] = useState<"in_flight" | "planned">("in_flight");
  const [window_, setWindow] = useState("");
  const [refsError, setRefsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (props.autoFocus) areaRef.current?.focus();
  }, [props.autoFocus]);

  const parsed = parseRefInput(refsText, repos);
  const refsOk = "refs" in parsed;
  const canAdd = meaning.trim().length > 0 && feature.trim().length > 0 && (state === "planned" || (repos.length > 0 && refsOk && parsed.refs.length > 0));

  const toggleRepo = (r: ReleaseRepo) => setRepos((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const submit = async () => {
    if (!canAdd || !refsOk) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiFetch<{ change: Change }>("/api/admin/releases/changes", {
        method: "POST",
        body: { feature: feature.trim(), meaning: meaning.trim(), repos, refs: refsBody(parsed.refs), state, planned_window: state === "planned" ? window_.trim() || null : null },
      });
      props.onRecorded(r.change);
      setMeaning("");
      setFeature("");
      setRepos([]);
      setRefsText("");
      setWindow("");
      setExpanded(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="collabComposer">
      <textarea
        ref={areaRef}
        className="collabComposerArea"
        aria-label="Record a change"
        placeholder="Record a change: what it is, in one line a user would understand…"
        value={meaning}
        maxLength={300}
        onFocus={() => setExpanded(true)}
        onChange={(e) => setMeaning(e.target.value)}
      />
      <button type="button" className="btn collabBtnAmber" disabled={!canAdd || busy} onClick={() => void submit()}>
        Add
      </button>
      {expanded ? (
        <div className="collabComposerMore">
          <label className="field">
            <span className="label">Feature</span>
            <input className="input" list="releases-feature-names" value={feature} maxLength={120} placeholder="The feature this change belongs to" onChange={(e) => setFeature(e.target.value)} />
            <datalist id="releases-feature-names">
              {props.featureNames.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </label>
          <div className="field">
            <span className="label">Repos</span>
            <div className="collabChipRow">
              {RELEASE_REPOS.map((r) => (
                <button key={r} type="button" className={`collabRepoChoice${repos.includes(r) ? " isOn" : ""}`} aria-pressed={repos.includes(r)} onClick={() => toggleRepo(r)}>
                  {r}
                </button>
              ))}
            </div>
          </div>
          <label className="field">
            <span className="label">Pull requests or commit{state === "in_flight" ? " (required for anything in flight)" : ""}</span>
            <input
              className="input"
              value={refsText}
              placeholder={repos.length > 1 ? "hub #283, console #215" : "#285 or 629996e"}
              onChange={(e) => {
                setRefsText(e.target.value);
                setRefsError(null);
              }}
              onBlur={() => setRefsError("error" in parsed ? parsed.error : null)}
            />
            {refsError ? <div className="help" style={{ color: "var(--danger-text)" }}>{refsError}</div> : null}
          </label>
          <div className="inputRow">
            <label className="field">
              <span className="label">State</span>
              <select className="select" value={state} onChange={(e) => setState(e.target.value as "in_flight" | "planned")}>
                <option value="in_flight">In flight — on dev, not deployed</option>
                <option value="planned">Planned — in design</option>
              </select>
            </label>
            {state === "planned" ? (
              <label className="field">
                <span className="label">Window (blank = not scheduled)</span>
                <input className="input" value={window_} maxLength={100} placeholder="week of Nov 9" onChange={(e) => setWindow(e.target.value)} />
              </label>
            ) : null}
          </div>
          {error ? <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div> : null}
        </div>
      ) : null}
      <div className="collabComposerHint">
        <span>Repo and pull request are required for anything in flight or shipped.</span>
        <span>
          Through the platform door: <code>record_change</code> · <code>plan_deploy</code> · <code>record_deploy</code> · <code>record_maintenance</code>
        </span>
      </div>
    </div>
  );
}

function InFlightTab(props: { onCounts: (c: Counts) => void; autoFocusComposer: boolean }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [changes, setChanges] = useState<Change[]>([]);
  const [featureNames, setFeatureNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await apiFetch<{ changes: Change[]; feature_names: string[]; counts: Counts }>("/api/admin/releases?state=in_flight");
      setChanges(r.changes);
      setFeatureNames(r.feature_names);
      props.onCounts(r.counts);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (c: Change) => {
    const ok = await confirm.confirm({ title: "Remove this change?", detail: `${c.feature} — it has not shipped, so nothing is lost but the row.`, confirmLabel: "Remove" });
    if (!ok) return;
    try {
      await apiFetch(`/api/admin/releases/changes/${encodeURIComponent(c.id)}`, { method: "DELETE" });
      toast.push({ kind: "success", message: "Removed." });
      void load();
    } catch (e) {
      toast.push({ kind: "error", message: errorMessage(e) });
    }
  };

  if (error) return <ErrorState detail={error} />;
  if (loading) return <LoadingState />;
  return (
    <div>
      <div className="collabSecHead">
        <span className="collabCount">On dev, not yet deployed</span>
      </div>
      <div className="collabPlan">
        <Composer
          featureNames={featureNames}
          autoFocus={props.autoFocusComposer}
          onRecorded={(c) => {
            toast.push({ kind: "success", message: c.state === "planned" ? "Recorded as planned." : "Recorded as in flight." });
            void load();
          }}
        />
        {changes.length === 0 ? <EmptyState title="Nothing in flight — record what is on dev." /> : null}
        {changes.map((c) => (
          <div key={c.id} className="collabPlanRow" title="Ships when its deploy is recorded — record_deploy at the door moves this row.">
            <div className="collabPlanWhen">
              {c.entered_dev_at ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(c.entered_dev_at)) : "—"}
              <small>on dev</small>
            </div>
            <div>
              <FeatureName change={c} onSaved={(n) => setChanges((prev) => prev.map((x) => (x.id === n.id ? n : x)))} />
              <MeaningLine change={c} onSaved={(n) => setChanges((prev) => prev.map((x) => (x.id === n.id ? n : x)))} />
            </div>
            <div className="collabRowMeta">
              <Token kind="in_flight" />
              <RepoChips repos={c.repos} />
              <RefChips change={c} />
              <button type="button" className="adminListAction isDanger" onClick={() => void remove(c)}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Planned (§3.4)
// ─────────────────────────────────────────────────────────────────────────────

function PlanDeployDialog(props: { onClose: () => void; onPlanned: (g: DeployGroup) => void }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [candidates, setCandidates] = useState<Change[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void apiFetch<{ changes: Change[] }>("/api/admin/releases?state=in_flight")
      .then((r) => setCandidates(r.changes.filter((c) => !c.deploy_id)))
      .catch((e) => setError(errorMessage(e)));
  }, []);
  const at = plannedAt(date, time);
  const submit = async () => {
    if (!at) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiFetch<{ deploy: DeployGroup }>("/api/admin/releases/deploys", { method: "POST", body: { kind: "planned", at, change_ids: picked } });
      props.onPlanned(r.deploy);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <div className="modalOverlay" onClick={props.onClose}>
      <div className="modal collabModal" role="dialog" aria-modal="true" aria-label="Plan a deploy" onClick={(e) => e.stopPropagation()}>
        <h2 className="collabH2">Plan a deploy</h2>
        <div style={{ display: "grid", gap: 10 }}>
          <div className="inputRow">
            <label className="field">
              <span className="label">Date</span>
              <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <label className="field">
              <span className="label">Time</span>
              <input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </label>
          </div>
          <div className="field">
            <span className="label">What it carries</span>
            {candidates.length === 0 ? (
              <div className="help">Nothing in flight to pick — when the deploy is recorded, it matches what it carried.</div>
            ) : (
              <div className="collabPickList">
                {candidates.map((c) => (
                  <label key={c.id} className="collabPick">
                    <input type="checkbox" checked={picked.includes(c.id)} onChange={(e) => setPicked((prev) => (e.target.checked ? [...prev, c.id] : prev.filter((x) => x !== c.id)))} />
                    <span>
                      <b>{c.feature}</b> <span className="muted">{c.meaning}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {error ? <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div> : null}
          <div className="actions">
            <button type="button" className="btn" onClick={props.onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btnPrimary" onClick={() => void submit()} disabled={busy || !at}>
              Plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlannedTab(props: { onCounts: (c: Counts) => void; planOpen: boolean; onPlanClose: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [deploys, setDeploys] = useState<DeployGroup[]>([]);
  const [changes, setChanges] = useState<Change[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await apiFetch<{ deploys: DeployGroup[]; changes: Change[]; counts: Counts }>("/api/admin/releases?state=planned");
      setDeploys(r.deploys);
      setChanges(r.changes);
      props.onCounts(r.counts);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeChange = async (c: Change) => {
    const ok = await confirm.confirm({ title: "Remove this planned change?", detail: c.feature, confirmLabel: "Remove" });
    if (!ok) return;
    try {
      await apiFetch(`/api/admin/releases/changes/${encodeURIComponent(c.id)}`, { method: "DELETE" });
      void load();
    } catch (e) {
      toast.push({ kind: "error", message: errorMessage(e) });
    }
  };
  const removeDeploy = async (g: DeployGroup) => {
    const ok = await confirm.confirm({ title: "Drop this planned deploy?", detail: `${g.date_words}, ${g.time_words} — its picked changes stay in flight.`, confirmLabel: "Drop" });
    if (!ok) return;
    try {
      await apiFetch(`/api/admin/releases/deploys/${encodeURIComponent(g.id)}`, { method: "DELETE" });
      void load();
    } catch (e) {
      toast.push({ kind: "error", message: errorMessage(e) });
    }
  };

  if (error) return <ErrorState detail={error} />;
  if (loading) return <LoadingState />;
  return (
    <div>
      {props.planOpen ? (
        <PlanDeployDialog
          onClose={props.onPlanClose}
          onPlanned={() => {
            props.onPlanClose();
            toast.push({ kind: "success", message: "Deploy planned." });
            void load();
          }}
        />
      ) : null}
      <div className="collabSecHead">
        <span className="collabCount">What's coming, with its window</span>
      </div>
      {deploys.length === 0 && changes.length === 0 ? <EmptyState title="Nothing planned — Plan a deploy, or record a change as planned." /> : null}
      {deploys.length ? (
        <div className="collabDeployList" style={{ marginBottom: 14 }}>
          {deploys.map((g) => (
            <div key={g.id}>
              <DeployGroupCard group={g} onChanged={(n) => setDeploys((prev) => prev.map((x) => (x.id === n.id ? n : x)))} />
              <div className="collabPlanActions">
                <button type="button" className="adminListAction isDanger" onClick={() => void removeDeploy(g)}>
                  Drop this planned deploy
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <div className="collabPlan">
        {changes.map((c) => (
          <div key={c.id} className="collabPlanRow">
            <div className={`collabPlanWhen${c.planned_window ? "" : " isNone"}`}>{c.planned_window ?? "Not scheduled"}</div>
            <div>
              <FeatureName change={c} onSaved={(n) => setChanges((prev) => prev.map((x) => (x.id === n.id ? n : x)))} />
              <MeaningLine change={c} onSaved={(n) => setChanges((prev) => prev.map((x) => (x.id === n.id ? n : x)))} />
            </div>
            <div className="collabRowMeta">
              <Token kind="planned" />
              <RepoChips repos={c.repos} />
              <RefChips change={c} />
              <button type="button" className="adminListAction isDanger" onClick={() => void removeChange(c)}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// This week (§3.5)
// ─────────────────────────────────────────────────────────────────────────────

function ThisWeekTab() {
  const [week, setWeek] = useState<WeekView | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void apiFetch<{ week: WeekView }>("/api/admin/releases/week")
      .then((r) => setWeek(r.week))
      .catch((e) => setError(errorMessage(e)));
  }, []);
  if (error) return <ErrorState detail={error} />;
  if (!week) return <LoadingState />;
  const r = week.renderings;
  return (
    <div>
      <div className="collabSecHead">
        <span className="collabCount">{week.words} · what a workspace sees of it</span>
      </div>
      <div className="collabRail collabRail2">
        <div className="collabBox">
          <h3>Since Monday</h3>
          <div className="collabStat">
            <b>{week.counts.deploys}</b>
            <span>deploys since Monday</span>
          </div>
          <div className="collabStat">
            <b>{week.counts.shipped}</b>
            <span>changes shipped</span>
          </div>
          <div className="collabStat">
            <b>{week.counts.in_flight}</b>
            <span>in flight</span>
          </div>
          <div className="collabStat">
            <b>{week.counts.scheduled}</b>
            <span>scheduled</span>
          </div>
        </div>
        <div className="collabBox">
          <h3>As a workspace reads it</h3>
          <div className="collabTenantView">
            <span className="collabCap">Collab · This week</span>
            <span className="collabT">{r.collab_panel.title}</span>
            {r.collab_panel.lines.length ? (
              <ul>
                {r.collab_panel.lines.map((l) => (
                  <li key={l}>{l}</li>
                ))}
                {r.collab_panel.more > 0 ? <li>+{r.collab_panel.more} more</li> : null}
              </ul>
            ) : (
              <span className="muted">Nothing shipped this week.</span>
            )}
          </div>
          <div className="collabSlide">
            <span className="collabSlideK">{r.deck_slide.kicker}</span>
            <span className="collabSlideH">{r.deck_slide.heading}</span>
            {r.deck_slide.lines.length ? (
              <ul>
                {r.deck_slide.lines.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            ) : null}
          </div>
          <div className="collabDoorLine">
            An assistant answers “what changed in Atlas this week?” from the same rows: <em>{r.assistant_line}</em>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The page
// ─────────────────────────────────────────────────────────────────────────────

export function AdminReleases(props: { tab: ReleasesTab }) {
  const [counts, setCounts] = useState<Counts | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [recordFocus, setRecordFocus] = useState(false);
  useEffect(() => {
    if (props.tab !== "in-flight") setRecordFocus(false);
    if (props.tab !== "planned") setPlanOpen(false);
  }, [props.tab]);

  return (
    <PageLayout
      title="Releases"
      breadcrumb={<span className="collabKicker">Atlas platform</span>}
      actions={
        <>
          <Link className="btn" to="/admin/releases/planned" onClick={() => setPlanOpen(true)}>
            Plan a deploy
          </Link>
          <Link className="btn collabBtnAmber" to="/admin/releases/in-flight" onClick={() => setRecordFocus(true)}>
            Record a change
          </Link>
        </>
      }
    >
      <div className="collabPage">
        <nav className="tabs collabTabs" aria-label="Releases">
          {RELEASES_TABS.map((t) => (
            <Link key={t.key} to={t.href} className={`tab${props.tab === t.key ? " tabActive" : ""}`} aria-current={props.tab === t.key ? "page" : undefined}>
              {t.label}
              {t.count && counts ? <span className="collabTabCount">{counts[t.count]}</span> : null}
            </Link>
          ))}
        </nav>
        {props.tab === "shipped" ? <ShippedTab onCounts={setCounts} /> : null}
        {props.tab === "in-flight" ? <InFlightTab onCounts={setCounts} autoFocusComposer={recordFocus} /> : null}
        {props.tab === "planned" ? <PlannedTab onCounts={setCounts} planOpen={planOpen} onPlanClose={() => setPlanOpen(false)} /> : null}
        {props.tab === "this-week" ? <ThisWeekTab /> : null}
      </div>
    </PageLayout>
  );
}
